import { useState, useEffect } from 'react';
import { Video, VideoOff, Mic, MicOff, PhoneOff, MessageSquare, Users, Settings, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';

interface VideoConsultationProps {
  onBack?: () => void;
  doctorName?: string;
}

export function VideoConsultation({ onBack, doctorName = 'Dr. Meera Krishnan' }: VideoConsultationProps) {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [chatMessage, setChatMessage] = useState('');

  const chatMessages = [
    { sender: 'Doctor', message: 'Hello! How are you feeling today?', time: '10:05' },
    { sender: 'You', message: 'Good morning doctor. I am feeling much better.', time: '10:06' },
    { sender: 'Doctor', message: 'Great to hear! Let\'s review your progress.', time: '10:06' },
  ];

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isCallActive) {
      interval = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isCallActive]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartCall = () => {
    setIsCallActive(true);
    toast.success('Connected to video call');
  };

  const handleEndCall = () => {
    setIsCallActive(false);
    setCallDuration(0);
    toast.info('Call ended');
    onBack?.();
  };

  const handleToggleMute = () => {
    setIsMuted(!isMuted);
    toast.info(isMuted ? 'Microphone on' : 'Microphone muted');
  };

  const handleToggleVideo = () => {
    setIsVideoOn(!isVideoOn);
    toast.info(isVideoOn ? 'Camera off' : 'Camera on');
  };

  const handleSendMessage = () => {
    if (chatMessage.trim()) {
      toast.success('Message sent');
      setChatMessage('');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && !isCallActive && (
            <button onClick={onBack} className="text-primary-foreground">
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}
          <div>
            <h1 className="text-primary-foreground">Video Consultation</h1>
            <p className="text-primary-foreground/80">{doctorName}</p>
          </div>
        </div>
        {isCallActive && (
          <Badge className="bg-accent text-accent-foreground">
            {formatDuration(callDuration)}
          </Badge>
        )}
      </div>

      <div className="relative h-[calc(100vh-80px)]">
        {/* Main Video Area */}
        <div className="absolute inset-0 bg-[#1a1a1a] flex items-center justify-center">
          {!isCallActive ? (
            <div className="text-center space-y-6">
              <div className="w-32 h-32 rounded-full bg-primary mx-auto flex items-center justify-center">
                <Video className="w-16 h-16 text-primary-foreground" />
              </div>
              <div>
                <h2 className="text-white mb-2">Ready to join?</h2>
                <p className="text-white/70">Start your consultation with {doctorName}</p>
              </div>
              <Button 
                className="bg-accent hover:bg-accent/90 h-14 px-8 rounded-xl"
                onClick={handleStartCall}
              >
                <Video className="w-5 h-5 mr-2" />
                Start Call
              </Button>
            </div>
          ) : (
            <>
              {/* Doctor's Video Feed */}
              <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-48 h-48 rounded-full bg-primary mx-auto flex items-center justify-center mb-4">
                    <span className="text-primary-foreground text-6xl">MK</span>
                  </div>
                  <h2 className="text-white">{doctorName}</h2>
                  <p className="text-white/70">Pulmonologist</p>
                </div>
              </div>

              {/* Your Video (Picture-in-Picture) */}
              <div className="absolute top-4 right-4 w-48 h-36 bg-[#2a2a2a] rounded-xl overflow-hidden shadow-2xl border-2 border-white/20">
                <div className="w-full h-full bg-gradient-to-br from-accent/30 to-primary/30 flex items-center justify-center">
                  {isVideoOn ? (
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-accent mx-auto flex items-center justify-center">
                        <span className="text-accent-foreground">RK</span>
                      </div>
                      <p className="text-white mt-2">You</p>
                    </div>
                  ) : (
                    <div className="text-white/50">
                      <VideoOff className="w-8 h-8 mx-auto" />
                      <p className="mt-2">Camera Off</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Call Controls */}
        {isCallActive && (
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={handleToggleMute}
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                  isMuted ? 'bg-destructive' : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                {isMuted ? (
                  <MicOff className="w-6 h-6 text-white" />
                ) : (
                  <Mic className="w-6 h-6 text-white" />
                )}
              </button>

              <button
                onClick={handleToggleVideo}
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                  !isVideoOn ? 'bg-destructive' : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                {isVideoOn ? (
                  <Video className="w-6 h-6 text-white" />
                ) : (
                  <VideoOff className="w-6 h-6 text-white" />
                )}
              </button>

              <button
                onClick={handleEndCall}
                className="w-16 h-16 rounded-full bg-destructive hover:bg-destructive/90 flex items-center justify-center transition-colors"
              >
                <PhoneOff className="w-7 h-7 text-white" />
              </button>

              <button
                onClick={() => setShowChat(!showChat)}
                className="w-14 h-14 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors relative"
              >
                <MessageSquare className="w-6 h-6 text-white" />
                {showChat && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full" />
                )}
              </button>

              <button className="w-14 h-14 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                <Settings className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>
        )}

        {/* Chat Panel */}
        {showChat && isCallActive && (
          <div className="absolute right-0 top-0 bottom-0 w-96 bg-white border-l border-border shadow-2xl">
            <div className="h-full flex flex-col">
              <div className="p-4 border-b border-border">
                <h3 className="text-primary">Chat</h3>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatMessages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.sender === 'You' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-xl p-3 ${
                        msg.sender === 'You'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary text-foreground'
                      }`}
                    >
                      <p>{msg.message}</p>
                      <p
                        className={`mt-1 ${
                          msg.sender === 'You'
                            ? 'text-primary-foreground/70'
                            : 'text-muted-foreground'
                        }`}
                      >
                        {msg.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 border-t border-border">
                <div className="flex gap-2">
                  <Input
                    placeholder="Type a message..."
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  />
                  <Button 
                    className="bg-accent hover:bg-accent/90"
                    onClick={handleSendMessage}
                  >
                    Send
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Waiting Room Info */}
        {!isCallActive && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
            <Card className="shadow-2xl">
              <CardContent className="p-6 text-center space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-primary">Your appointment</h3>
                    <p className="text-muted-foreground">Scheduled for 10:00 AM</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={handleToggleMute}
                    className={`flex-1 p-3 rounded-xl flex items-center justify-center gap-2 ${
                      isMuted ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
                    }`}
                  >
                    {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                    <span>{isMuted ? 'Mic Off' : 'Mic On'}</span>
                  </button>
                  <button
                    onClick={handleToggleVideo}
                    className={`flex-1 p-3 rounded-xl flex items-center justify-center gap-2 ${
                      !isVideoOn ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
                    }`}
                  >
                    {isVideoOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
                    <span>{isVideoOn ? 'Cam On' : 'Cam Off'}</span>
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
